import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import NeilMethodSection from "@/components/NeilMethodSection";
import SEO from "@/components/SEO";
import HumanoidTabs from "@/components/HumanoidTabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Check, Cpu } from "lucide-react";
import TechMedia from "@/components/TechMedia";
import nexoraHero from "@/assets/nexora/nexora-mini-hero.jpg.asset.json";
import atlasSpec from "@/assets/nexora/nexora-atlas-spec.jpeg.asset.json";
import platformSpecs from "@/assets/humanoid-healthcare/hh-0783.jpeg.asset.json";
import squatLift from "@/assets/humanoid-healthcare/hh-0786.jpeg.asset.json";
import waistReach from "@/assets/humanoid-healthcare/hh-0787.jpeg.asset.json";
import batterySwap from "@/assets/humanoid-healthcare/hh-0784.jpeg.asset.json";
import batteryMonitor from "@/assets/humanoid-healthcare/hh-0785.jpeg.asset.json";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const PLATFORMS = [
  {
    name: "NEXORA MINI™",
    image: nexoraHero.url,
    status: "Available for pre-order",
    body: "A compact wheeled humanoid platform for education, research, reception, and light service tasks. Standard and EDU configurations, eight color options.",
    points: ["Education and research programs", "Reception and guest engagement", "Developer-friendly EDU configuration"],
    href: "/nexora",
  },
  {
    name: "NEXORA ATLAS™",
    image: atlasSpec.url,
    status: "Deployment programs open",
    body: "A full-size humanoid platform built on NVIDIA Jetson AGX Orin with LiDAR and depth perception, targeting caregiving support, elderly care assistance, home services, and R&D.",
    points: ["NVIDIA Jetson AGX Orin compute", "LiDAR + depth perception", "Under 35 kg frame"],
    href: "/nexora",
  },
  {
    name: "Industrial Humanoid Programs",
    image: null,
    status: "Consultation required",
    body: "For manufacturing, logistics, and enterprise service environments, Neil AI evaluates industrial humanoid and bipedal platforms against your actual workflow before recommending a deployment path. Availability, configuration, and timelines are confirmed per project.",
    points: ["Workflow and safety assessment first", "Pilot-before-scale deployment model", "Integration with existing operations"],
    href: null,
  },
];

const FLEET_CLASSES = [
  {
    id: "industrial",
    name: "Industrial Humanoid — Continuous Operation Class",
    tag: "Manufacturing & logistics",
    body: "Bipedal industrial humanoid built for 24/7 lines. Autonomous battery swapping keeps the unit working through shift changes instead of docking to charge.",
    specs: [
      "Autonomous battery swap in under 3 minutes",
      "Dual-battery dynamic balancing for 24/7 operation",
      "15 kg payload across a 0–1.8 m working range",
      "±162° waist rotation, 125° pitch",
      "Binocular RGB stereo vision perception",
      "Fleet/swarm coordination across production lines",
    ],
  },
  {
    name: "Industrial Humanoid — Task Automation Class",
    tag: "Assembly, inspection & material handling",
    body: "Bipedal humanoid using LLM-based task planning and semantic visual SLAM navigation for dexterous manipulation in structured industrial environments.",
    specs: [
      "LLM-based task planning",
      "Semantic VSLAM navigation",
      "Learning-based whole-body motion control",
      "Dexterous manipulation with stable walking",
    ],
  },
  {
    id: "commercial",
    name: "Wheeled Service Humanoid — Enterprise Class",
    tag: "Warehouse, retail & enterprise service",
    body: "Full-size wheeled humanoid with a bidirectional-bending torso, engineered for narrow aisles and repetitive lift-and-place work alongside staff.",
    specs: [
      "±170° rotation within 0.8 m passages",
      "0–40 cm lifting range",
      "15 kg payload across a 0–1.8 m working range",
      "2 m/s travel speed with self-balancing on complex terrain",
      "Fourth-generation dexterous hands, sub-millimeter manipulation",
      "Binocular RGB stereo vision",
    ],
  },
  {
    id: "guest-engagement",
    name: "Guest Engagement Humanoid — Bipedal",
    tag: "Hospitality, healthcare & public venues",
    body: "Walking humanoid for guided tours, reception, wayfinding, and guest engagement in lobbies, hospitals, campuses, and visitor centers.",
    specs: [
      "163 cm height, 43 kg weight",
      "20 degrees of freedom",
      "2 h walking / 4 h standing runtime, 1.5 h charge",
      "6 km/h walking speed",
      "Dual RGBD plus dual structured-light 3D cameras",
      "500 mm arm wingspan, high-precision IMU",
    ],
  },
  {
    id: "concierge",
    name: "Concierge Humanoid — Cloud Service Class",
    tag: "Reception, retail & customer service",
    body: "Cloud-connected service humanoid using large language models for live consultation, multimodal greeting, guided tours, and customizable service scripts.",
    specs: [
      "LLM-powered consultation",
      "Multimodal greeting and guided tours",
      "Cloud fleet management",
      "Customizable service workflows",
    ],
  },
  {
    id: "research",
    name: "Education & Research Humanoids",
    tag: "Schools, universities & workforce training",
    body: "Desktop humanoid and modular robotics platforms for STEM programs, AI curriculum, and developer research — from block-based coding through Python and C/C++.",
    specs: [
      "14–17 DOF desktop humanoid platforms from 700 g",
      "Onboard vision, mic arrays, and motion sensing",
      "Modular kits with 1 TOPS onboard AI compute",
      "Python, C/C++, Java, and block-based coding support",
      "Micro:bit, Arduino, and Raspberry Pi compatible",
    ],
  },
];

const HumanoidRobotics = () => (
  <>
    <SEO
      title="Humanoid Robotics Programs | Neil AI Global Solutions"
      description="Humanoid robotics from Neil AI: NEXORA MINI and NEXORA ATLAS platforms plus industrial humanoid evaluation programs for enterprise operations."
      keywords="humanoid robots, NEXORA ATLAS, NEXORA MINI, service humanoid robot, enterprise humanoid robotics"
    />
    <Navigation />

    <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
      <div className="max-w-7xl mx-auto px-4">
        <HumanoidTabs />

        <section className="mt-6 rounded-3xl border border-[hsl(var(--line))] glass-effect p-8 md:p-14">
          <Badge className="mb-4 bg-primary/15 text-primary border border-primary/30">
            Humanoid Division
          </Badge>
          <h1 className="font-orbitron text-3xl md:text-5xl font-bold max-w-3xl leading-tight">
            The Next Evolution of Autonomous Work
          </h1>
          <p className="mt-5 max-w-3xl text-muted-foreground md:text-lg">
            Humanoid platforms are moving from demonstration to real deployment. Neil AI takes a
            grounded approach: we assess the workflow, confirm what a platform can actually do today,
            pilot it in your environment, then scale. No speculative capability claims.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Design My Humanoid Pilot <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full">
              <Link to="/nexora">Explore NEXORA</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full">
              <Link to="/robots/humanoid/healthcare">Healthcare &amp; Senior Care</Link>
            </Button>
          </div>
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-3">
          {PLATFORMS.map((p) => (
            <Card key={p.name} className="glass-effect border-[hsl(var(--line))] overflow-hidden flex flex-col">
              {p.image && (
                <div className="aspect-[16/10] bg-white/5 p-3 sm:p-5">
                  <img src={p.image} alt={p.name} className="h-full w-full object-contain" loading="lazy" />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <Badge variant="outline" className="w-fit text-[11px]">{p.status}</Badge>
                <h2 className="mt-3 font-orbitron text-lg font-bold">{p.name}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex gap-2 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-2 mt-auto">
                  {p.href ? (
                    <Button asChild size="sm" className="rounded-full w-full">
                      <Link to={p.href}>Learn More</Link>
                    </Button>
                  ) : (
                    <Button asChild size="sm" variant="outline" className="rounded-full w-full">
                      <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                        Request Consultation
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </section>

        <section id="specifications" className="mt-16 scroll-mt-40">
          <Badge className="mb-4 bg-accent/15 text-accent border border-accent/30">Humanoid Fleet Classes</Badge>
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold max-w-3xl leading-tight">
            The Full Humanoid Fleet, Organized By Function
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Neil AI sources humanoid platforms from vetted global manufacturers and deploys them under one
            program. Select by the work you need done — every class below is available through a Neil AI
            assessment, pilot, and Robotics-as-a-Service agreement.
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {FLEET_CLASSES.map((c) => (
              <Card key={c.name} id={(c as { id?: string }).id} className="glass-effect border-[hsl(var(--line))] p-6 flex flex-col scroll-mt-40">
                <Badge variant="outline" className="w-fit text-[11px]">{c.tag}</Badge>
                <h3 className="mt-3 font-orbitron text-base font-bold leading-snug">{c.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
                <ul className="mt-4 space-y-1.5">
                  {c.specs.map((s) => (
                    <li key={s} className="flex gap-2 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-2 mt-auto">
                  <Button asChild size="sm" variant="outline" className="rounded-full w-full">
                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                      Request Specs & Pricing
                    </a>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* CONTINUOUS OPERATION */}
        <section id="continuous-operation" className="mt-20 scroll-mt-40">
          <Badge className="mb-4 bg-accent/15 text-accent border border-accent/30">Uptime &amp; Endurance</Badge>
          <h2 className="font-orbitron text-2xl md:text-4xl font-bold max-w-3xl leading-tight">
            Built for Continuous Operation
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Work runs around the clock. Industrial-class humanoid platforms are engineered for sustained
            duty cycles, high-payload handling and minimal downtime.
          </p>

          <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={platformSpecs.url}
              alt="Full-size humanoid platform specifications: size, waist articulation, perception and dexterous hands"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Full-Size Platform Specifications</h3>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {[
                  "Height 176 cm · Weight 70 kg",
                  "52 degrees of freedom",
                  "Humanoid gait, walking up to 2 m/s",
                  "Waist pitch +90° to -35°, rotation ±162°",
                  "LLM voice interaction · 4-inch facial display",
                  "4 array microphones · 2 speakers",
                  "Gen-4 dexterous hands · 11 DOF",
                  "Single-hand grasp 7.5 kg · finger grasp 1 kg",
                ].map((s) => (
                  <li key={s} className="flex gap-2 text-xs text-muted-foreground">
                    <Cpu className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="lg:order-2">
              <h3 className="font-orbitron text-xl font-bold">Deep Squat &amp; Stoop Lifting</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                A whole-body dynamic balancing algorithm allows a 125° pitch angle and 15 kg payload
                handling within a 0–1.8 m workspace — floor-level pickup through shelf-height placement
                without a fixed workstation.
              </p>
            </div>
            <TechMedia
              className="lg:order-1"
              src={squatLift.url}
              alt="Humanoid platform performing a deep squat lift of a box in a logistics environment"
            />
          </div>

          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={waistReach.url}
              alt="Humanoid platform reaching into warehouse shelving with extended waist rotation"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Extended Reach Without Repositioning</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                A high-power, high-torque waist servo delivers ±162° rotation and 15 kg lift capability,
                enabling ground-reaching flexibility and extended-reach manipulation across aisles, carts
                and shelving.
              </p>
            </div>
          </div>

          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="lg:order-2">
              <h3 className="font-orbitron text-xl font-bold">Autonomous Battery Swapping</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Dual-battery dynamic balancing and dual-arm coordinated precision allow the platform to swap
                its own battery in roughly three minutes — supporting 24/7 continuous operation without a
                technician on standby.
              </p>
            </div>
            <TechMedia
              className="lg:order-1"
              src={batterySwap.url}
              alt="Humanoid platform autonomously swapping its battery at a swap station"
            />
          </div>

          <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
            <TechMedia
              src={batteryMonitor.url}
              alt="Humanoid platform beside a multi-bay battery swap station"
            />
            <div>
              <h3 className="font-orbitron text-xl font-bold">Intelligent Power Replenishment</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Real-time battery monitoring lets the platform switch between dual-battery and
                single-battery modes and choose swap or charge based on task priority — dynamic power
                management tuned to the shift, not the charger.
              </p>
            </div>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Endurance figures reflect logistics and manufacturing duty cycles. Care-setting deployments are
            scoped separately and always operate under staff supervision.
          </p>
        </section>

        <p className="mt-10 text-xs text-muted-foreground">
          Humanoid platform capabilities, availability, and timelines vary by manufacturer program and
          are confirmed during consultation. Neil AI Global Solutions provides evaluation, deployment,
          integration, training, and support.
        </p>
      </div>
    </main>

    <div className="px-4"><NeilMethodSection compact contextLabel="N.E.I.L.™ HUMANOID USE-CASE ASSESSMENT" contextCopy="Humanoid platforms are matched to validated use cases. We define the task, evaluate the environment, itemize existing resources, and lock the operating baseline before recommending a configuration." /></div>
        <Footer />
  </>
);

export default HumanoidRobotics;
